import { FileIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import FileViewer from '@components/FileViewer/FileViewer'
import { getFileExtension, isViewableImage, openFileInNewTab } from '@components/FileViewer/fileHelpers'
import { isPdfMime } from '@lib/mime'
import { PublicSectionGate } from '@components/PublicPage'
import { EmptyState } from '@components/TripOverview'
import { usePublicFiles } from '@hooks'
import { GetFileType } from '@orbit/api-types'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
	FileCard,
	FileGrid,
	FileInfo,
	FileName,
	FileThumbnail,
	FileTypeIcon,
} from '../Files/FilesPage.styles'
import PublicSectionLayout from './PublicSectionLayout'

/**
 * Public, read-only trip files at `/shared/trips/:tripCode/files` — the full
 * list the overview's ≤6-file slice deep-links to. Reuses the member
 * `FilesPage.styles` grid and the shared `FileViewer` lightbox, minus every
 * edit affordance (no upload, no delete). Inline-renderable images open in the
 * lightbox; everything else (including HEIC, which browsers can't decode)
 * opens in a new tab. Serves every file (vault + section attachments) — the
 * files section is a marketing-only surface enabled only on demo trips
 * (ADR 0003). Gating (loading / 404-unavailable / transient error) is the
 * shared `PublicSectionGate`.
 */
const PublicFilesPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const query = usePublicFiles(tripCode)

	const [viewerFile, setViewerFile] = useState<GetFileType | null>(null)

	const handleFileClick = (file: GetFileType) => {
		if (isViewableImage(file)) {
			setViewerFile(file)
		} else if (file.url) {
			openFileInNewTab(file.url)
		}
	}

	return (
		<PublicSectionGate
			query={query}
			loadingLabel="Loading files…"
			unavailableTitle="Files Unavailable"
			unavailableMessage="These files don't exist or haven't been shared publicly by the trip owner."
		>
			{data => {
				const files = data.files
				return (
					<PublicSectionLayout
						// Data present implies the query ran, which implies the code param existed.
						tripCode={tripCode ?? ''}
						title="Files"
						subtitle={`${countLabel(files.length, 'file')}`}
					>
						{files.length === 0 ? (
							<EmptyState
								icon={<FileIcon sx={{ fontSize: 32 }} />}
								text="No files have been added to this trip yet."
							/>
						) : (
							<FileGrid>
								{files.map(file => (
									<FileCard key={file.id} onClick={() => handleFileClick(file)}>
										<FileThumbnail $pdf={isPdfMime(file.mimeType)}>
											{isViewableImage(file) ? (
												<img src={file.url} alt={file.fileName} loading="lazy" />
											) : (
												<FileTypeIcon $pdf={isPdfMime(file.mimeType)}>
													<FileIcon sx={{ fontSize: 40 }} />
													<span>{getFileExtension(file.fileName)}</span>
												</FileTypeIcon>
											)}
										</FileThumbnail>
										<FileInfo>
											<FileName title={file.fileName}>{file.fileName}</FileName>
										</FileInfo>
									</FileCard>
								))}
							</FileGrid>
						)}

						<FileViewer file={viewerFile} onClose={() => setViewerFile(null)} />
					</PublicSectionLayout>
				)
			}}
		</PublicSectionGate>
	)
}

export default PublicFilesPage
